/**
 * <InstallConfirmModal> — install confirmation.
 *
 * Mockup §10 (`mockups/abilities/index.html#confirm`). The step between
 * clicking "Install" on the marketplace detail page and the actual install:
 * names everything that lands in the workspace so the choice is informed.
 *
 * Structure:
 *   - Header: brand-coloured icon hero, "Install <name>?" title, and a
 *     subtitle composed from the real counts ("8 skills and 1 connection
 *     for your agent.", install-copy.ts).
 *   - Body: per-category rows (skills · agents · connections) naming what
 *     lands in the workspace — human names, never kebab slugs.
 *   - Footer: Cancel + "Install <name>" buttons. Install button shows
 *     "Installing…" while POST is in-flight.
 *
 * Closes via: Cancel click, backdrop click, Esc keypress.
 *
 * Install flow: POSTs to `/api/abilities/install` via the `installAbility`
 * helper from `@/lib/api/abilities`. 201 → `onSuccess()`. 409
 * `already_installed` is treated as success (parent refreshes state either
 * way). Other errors surface inline via `role="alert"`.
 */
"use client";

import { useState, useEffect, ReactNode, Fragment } from "react";
import type { MarketplaceAbilityDetail } from "@bash/shared";
import { installAbility, uninstallAbility } from "@/lib/api/abilities";
import { invalidateAllAbilities } from "@/lib/abilities/cache";
import { isIconUrl } from "@/lib/abilities/icon";
import {
  installSummaryLine,
  prettifyItemName,
} from "@/lib/abilities/install-copy";
import { getIntegrationDisplayName } from "@/lib/composio-tools";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeaderRich,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { IntegrationIcon } from "@/components/ui/IntegrationIcons";
import { useToast } from "@/components/ui/Toast";

export interface InstallConfirmModalProps {
  uninstall?: boolean;
  open: boolean;
  ability: MarketplaceAbilityDetail | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const ERROR_COPY: Record<number, string> = {
  409: "Your agent is currently working on a task. Wait for it to finish, then try again.",
  503: "Couldn't reach your workspace. Try again in a moment.",
};

function errorMessage(status: number | null, fallback: string): string {
  if (status !== null && ERROR_COPY[status]) return ERROR_COPY[status];
  return fallback;
}

const ListItem = ({
  badge,
  badgeColorCl = "",
  label,
  items,
}: {
  badge: string;
  badgeColorCl: string;
  label: string;
  items: Array<{ name: string; icon?: ReactNode }>;
}) => {
  return (
    <li className="flex items-start gap-3 px-3 py-2.5 bg-bg-subtle rounded-md">
      <span className={`text-4 ${badgeColorCl}`} aria-hidden="true">
        {badge}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-3.5 font-medium text-fg-default">{label}</div>
        {items.length > 0 && (
          <div className="mt-1 flex flex-wrap items-center gap-x-1 gap-y-1 text-3 text-fg-muted">
            {items.map((m, idx) => (
              <Fragment key={m.name}>
                {idx > 0 ? <span>&middot;</span> : null}
                <span className="inline-flex items-center gap-1.5">
                  {m.icon}
                  {m.name}
                </span>
              </Fragment>
            ))}
          </div>
        )}
      </div>
    </li>
  );
};

export function InstallConfirmModal({
  uninstall,
  open,
  ability,
  onCancel,
  onSuccess,
}: InstallConfirmModalProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cachedAbility, setCachedAbility] = useState(ability);
  const { toast } = useToast();

  const displayAbility = ability || cachedAbility;

  useEffect(() => {
    if (ability) {
      setCachedAbility(ability);
      setError(null);
    }
  }, [ability]);

  if (!displayAbility) {
    return (
      <Dialog
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) onCancel();
        }}
      />
    );
  }

  const { skills, agents, mcps } = displayAbility.counts;
  const itemCount = skills + agents + mcps;

  async function handleInstall() {
    if (!displayAbility) return;
    setSaving(true);
    setError(null);
    try {
      await installAbility(displayAbility.slug);
      invalidateAllAbilities();
      toast(
        `Installed ${displayAbility.name} — your next message will have it.`
      );
      onSuccess();
    } catch (e) {
      // Atomic install: a thrown error means nothing was added to the volume
      // and the DB row was rolled back. Be honest about it.
      setError(
        "Install failed — nothing was added to your workspace. Try again?"
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleUninstall() {
    if (!displayAbility) return;
    setSaving(true);
    setError(null);
    try {
      await uninstallAbility(displayAbility.slug);
      invalidateAllAbilities();
      onSuccess();
    } catch (e) {
      // authedFetch surfaces failures as `Error("<status> <body…>")` — parse
      // the leading status code so we can pick the right copy. Anything that
      // doesn't match falls through to the original message.
      const msg = e instanceof Error ? e.message : String(e);
      const m = msg.match(/^(\d{3})\b/);
      const status = m ? Number(m[1]) : null;
      setError(errorMessage(status, msg));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel();
      }}
    >
      <DialogContent
        onInteractOutside={(e) => {
          if (saving) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (saving) e.preventDefault();
        }}
        hideCloseButton={saving}
        aria-labelledby={
          uninstall ? "uninstall-confirm-title" : "install-confirm-title"
        }
        aria-describedby={undefined}
      >
        {/* Header — icon hero + title + item summary */}
        <DialogHeaderRich
          icon={
            isIconUrl(displayAbility.icon) ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={displayAbility.icon}
                alt=""
                className="ab-chip-ico__photo"
              />
            ) : (
              (displayAbility.icon ?? "◇")
            )
          }
          iconBgcStyle={displayAbility.color}
          title={
            uninstall
              ? `Uninstall ${displayAbility.name}?`
              : `Install ${displayAbility.name}?`
          }
          titleId={
            uninstall ? "uninstall-confirm-title" : "install-confirm-title"
          }
          // Composed from the real counts ("8 skills and 1 connection for your agent.")
          // — never a hardcoded total.
          description={
            uninstall
              ? `Removes ${itemCount} ${itemCount === 1 ? "item" : "items"} from your workspace.`
              : // An ability is skills and connections, so the trailing
                // "plus N behaviors" clause never composes.
                installSummaryLine(displayAbility.counts, 0)
          }
        />

        {/* Body — per-category breakdown */}
        <div className="mt-5">
          <ul className="flex flex-col gap-y-1.5">
            {skills > 0 && (
              // Every skill, by its human name — wrapping beats an
              // ellipsis at the consent moment.
              <ListItem
                badge={"⁂"}
                badgeColorCl="text-indigo-700"
                label={`${skills} ${skills === 1 ? "skill" : "skills"}`}
                items={displayAbility.skills.map((s) => ({
                  name: prettifyItemName(s.name || s.slug, displayAbility.slug),
                }))}
              />
            )}
            {agents > 0 && (
              <ListItem
                badge={"◈"}
                badgeColorCl="text-amber-800"
                label={`${agents} ${agents === 1 ? "agent" : "agents"}`}
                items={displayAbility.agents.map((a) => ({
                  name: prettifyItemName(a.name || a.slug, displayAbility.slug),
                }))}
              />
            )}
            {mcps > 0 && (
              // The brand, not the plumbing: logo + display name
              // ("Higgsfield"), never a raw slug.
              <ListItem
                badge={"▶"}
                badgeColorCl="text-emerald-700"
                label={`${mcps} ${mcps === 1 ? "connection" : "connections"}`}
                items={displayAbility.mcps.map((m) => ({
                  icon: <IntegrationIcon integration={m.name} size={16} />,
                  name: getIntegrationDisplayName(m.name),
                }))}
              />
            )}
          </ul>

          {error && (
            <p
              role="alert"
              className="mt-3 rounded-8 border border-status-error/30 bg-status-error/5 px-3 py-2 text-3 text-status-error"
            >
              {error}
            </p>
          )}
        </div>

        {uninstall ? (
          <p className="mt-4 text-3 text-fg-subtle">
            Note: You can reinstall anytime from the marketplace.
          </p>
        ) : null}

        {/* Actions */}
        <DialogFooter className="flex flex-col sm:flex-col">
          <Button
            variant={uninstall ? "danger" : undefined}
            type="button"
            disabled={saving}
            className="w-full"
            onClick={uninstall ? handleUninstall : handleInstall}
          >
            {uninstall
              ? saving
                ? "Uninstalling…"
                : "Uninstall"
              : saving
                ? "Installing…"
                : `Install ${displayAbility.name}`}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={saving}
            className="w-full"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
